import React, { useMemo } from 'react';

import orderBy from 'lodash.orderby';

import { Separator } from '@/_components';
import { NoDataMessage, NoSectionMessage } from '@/_containers';

import { useDeleteNoteMutation } from '../shared/apis';
import { NotesItem } from '../shared/components/notes-item';
import type { NoteContextTypeSchema, NoteQuerySchema } from '../shared/schemas/notes-query-schema';

type NotesListProps = {
  notes: NoteQuerySchema[];
  contextType: NoteContextTypeSchema;
  contextId: string;
  isLoading?: boolean;
  maxMessageLength?: number;
};

export function NotesList({ notes, contextType, contextId, isLoading, maxMessageLength }: NotesListProps) {
  const deleteNoteMutation = useDeleteNoteMutation(contextType, contextId);

  // Sort notes by date (oldest first, so newest appears at bottom like messaging apps)
  const sortedNotes = useMemo(() => {
    return orderBy(notes, ['date'], ['asc']);
  }, [notes]);

  const handleDeleteNote = (noteId: number) => {
    deleteNoteMutation.mutate({ id: noteId });
  };

  if (isLoading) {
    return <NoSectionMessage message="Loading notes..." />;
  }

  if (sortedNotes.length === 0) {
    return (
      <div className="flex h-full flex-col">
        <div className="flex min-h-full flex-col p-4">
          <NoDataMessage message="No notes yet. Be the first to add a note." className="py-12" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-full flex-col pt-4">
      {sortedNotes.map((note, index) => (
        <React.Fragment key={note.id}>
          <NotesItem
            note={note}
            onDelete={handleDeleteNote}
            contextType={contextType}
            contextId={contextId}
            isDeleting={deleteNoteMutation.isPending}
            maxMessageLength={maxMessageLength}
          />
          {index < sortedNotes.length - 1 && <Separator className="my-4" />}
        </React.Fragment>
      ))}
    </div>
  );
}
